import {
  Camera,
  Check,
  ChevronLeft,
  Circle,
  Clock3,
  Eye,
  Globe2,
  Link2,
  Lock,
  Minus,
  Pause,
  Play,
  Plus,
  Pencil,
  RotateCcw,
  Send,
  Square,
  Volume2,
  X,
} from 'lucide-react'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { ANIMAL_AVATARS, animalAvatarUrl, avatarSrc } from '../../shared/avatars'
import { standingClaim, type Standing } from '../../shared/standing'
import { COUNTRY_CODES, HERE_FOR_OPTIONS, ROLE_CHIPS, countryFlag, countryName, hereForLabel } from '../../shared/profileOptions'
import type {
  ChatMessage,
  ClientIdentity,
  Intention,
  IntentionStep,
  LocationPrecision,
  SocialLinks,
  UserProfile,
} from '../../shared/types'
import { SESSION_BADGES, WEEK_BADGES, weekBadgeLabel } from '../../shared/types'
import { soundEngine, type SoundName, type StationName } from '../lib/soundEngine'
import { SessionBadge } from './VideoGrid'

export type PanelName = 'timer' | 'sounds' | 'intentions' | 'chat' | 'people'

interface PanelShellProps {
  title: string
  children: React.ReactNode
  onClose: () => void
}

export function PanelShell({ title, children, onClose }: PanelShellProps) {
  return (
    <aside className="side-panel">
      <header className="panel-header">
        <h2>{title}</h2>
        <button className="icon-button" onClick={onClose} aria-label="Close panel">
          <X size={21} />
        </button>
      </header>
      {children}
    </aside>
  )
}

export interface TimerControls {
  display: string
  durationMinutes: number
  running: boolean
  progress: number
  soundEnabled: boolean
  setSoundEnabled: (enabled: boolean) => void
  toggle: () => void
  setMinutes: (minutes: number) => void
  startWith: (minutes: number) => void
  adjust: (minutes: number) => void
  reset: () => void
}

interface TimerPanelProps {
  timer: TimerControls
  onClose: () => void
}

export function TimerPanel({ timer, onClose }: TimerPanelProps) {
  return (
    <PanelShell title="Timer" onClose={onClose}>
      <div className="timer-panel panel-scroll">
        <div className="timer-face">
          <button onClick={() => timer.adjust(-5)} aria-label="Subtract five minutes"><Minus /></button>
          <time>{timer.display}</time>
          <button onClick={() => timer.adjust(5)} aria-label="Add five minutes"><Plus /></button>
        </div>
        <div className="timer-actions">
          <button className="secondary-icon" onClick={timer.reset}><RotateCcw size={18} /></button>
          <button className="timer-play" onClick={timer.toggle}>
            {timer.running ? <Pause fill="currentColor" /> : <Play fill="currentColor" />}
          </button>
        </div>
        <div className="preset-row">
          {[10, 25, 50].map((minutes) => (
            <button
              className={timer.durationMinutes === minutes ? 'selected' : ''}
              key={minutes}
              onClick={() => timer.setMinutes(minutes)}
            >
              {minutes} mins
            </button>
          ))}
        </div>
        <label className="timer-sound-toggle">
          <span><Volume2 size={16} /> Play sound at end of timer</span>
          <input
            className="switch"
            type="checkbox"
            checked={timer.soundEnabled}
            onChange={(event) => timer.setSoundEnabled(event.target.checked)}
          />
        </label>
      </div>
    </PanelShell>
  )
}

const SOUND_ITEMS: Array<{ name: SoundName; label: string; emoji: string }> = [
  { name: 'cafe', label: 'Cafe', emoji: '☕' },
  { name: 'thunder', label: 'Thunderstorm', emoji: '⛈' },
  { name: 'waves', label: 'Beach waves', emoji: '🌊' },
  { name: 'fire', label: 'Fire crackling', emoji: '🔥' },
  { name: 'birds', label: 'Birds', emoji: '🐦' },
  { name: 'rain', label: 'Rain', emoji: '🌧' },
  { name: 'wind', label: 'Wind', emoji: '🍃' },
  { name: 'river', label: 'River', emoji: '🏞' },
  { name: 'whitenoise', label: 'White noise', emoji: '📻' },
  { name: 'night', label: 'Night crickets', emoji: '🌙' },
  { name: 'train', label: 'Train ride', emoji: '🚆' },
  { name: 'library', label: 'Library', emoji: '📚' },
]

const STATIONS: Array<{ name: StationName; title: string; emoji: string; description: string }> = [
  { name: 'lofi', title: 'Jazz lofi radio', emoji: '🌱', description: 'Easy lofi jazz, all day long' },
  { name: 'synthwave', title: 'Synthwave radio', emoji: '🌆', description: 'Retro synths with a modern pulse' },
  { name: 'piano', title: 'Calm piano radio', emoji: '🎹', description: 'Quiet piano for deep work' },
  { name: 'focus', title: 'Deep focus radio', emoji: '🌌', description: 'Ambient textures, zero distraction' },
]

export function SoundsPanel({ onToast, onClose }: { onToast: (text: string) => void; onClose: () => void }) {
  const [tab, setTab] = useState<'music' | 'sounds'>('music')
  const [active, setActive] = useState<Set<SoundName>>(() => new Set(soundEngine.activeNames()))
  const [station, setStation] = useState<StationName | null>(() => soundEngine.activeStation())
  const [volume, setVolume] = useState(() => soundEngine.getVolume())

  const toggle = async (name: SoundName) => {
    const label = SOUND_ITEMS.find((item) => item.name === name)?.label ?? name
    const playing = await soundEngine.toggle(name)
    onToast(playing ? `${label} on` : `${label} off`)
    setActive((current) => {
      const next = new Set(current)
      if (playing) next.add(name)
      else next.delete(name)
      return next
    })
  }

  const toggleStation = async (name: StationName) => {
    const title = STATIONS.find((entry) => entry.name === name)?.title ?? name
    const playing = await soundEngine.toggleStation(name)
    setStation(playing ? name : null)
    onToast(playing ? `${title} on` : `${title} off`)
  }

  return (
    <PanelShell title="Music & sounds" onClose={onClose}>
      <div className="sound-panel panel-scroll">
        <div className="panel-tabs">
          <button className={tab === 'music' ? 'active' : ''} onClick={() => setTab('music')}>🎶 Music</button>
          <button className={tab === 'sounds' ? 'active' : ''} onClick={() => setTab('sounds')}>🌿 Sounds</button>
        </div>
        <div className="sound-toolbar">
          {tab === 'sounds' && (
            <button
              onClick={() => {
                soundEngine.stopAll()
                setActive(new Set())
                setStation(null)
                onToast('All sound off')
              }}
            >
              <Square size={14} fill="currentColor" /> Stop all
            </button>
          )}
          <label>
            <Volume2 size={18} />
            <input
              aria-label="Volume"
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(event) => {
                const next = Number(event.target.value)
                setVolume(next)
                soundEngine.setVolume(next)
              }}
            />
          </label>
        </div>
        {tab === 'music' && (
          <div className="radio-list">
            {STATIONS.map((entry) => (
              <button
                className={`radio-card ${station === entry.name ? 'active' : ''}`}
                key={entry.name}
                onClick={() => void toggleStation(entry.name)}
              >
                <span className="radio-emoji">{entry.emoji}</span>
                <span className="radio-copy">
                  <strong>{entry.title}</strong>
                  <small>{entry.description}</small>
                </span>
                {station === entry.name
                  ? <span className="radio-playing"><Pause size={17} fill="currentColor" /></span>
                  : <span className="radio-play"><Play size={17} /></span>}
              </button>
            ))}
          </div>
        )}
        {tab === 'sounds' && (
          <div className="sound-grid">
            {SOUND_ITEMS.map((sound) => (
              <button
                className={active.has(sound.name) ? 'active' : ''}
                key={sound.name}
                onClick={() => void toggle(sound.name)}
              >
                <span>{sound.emoji}</span>
                <strong>{sound.label}</strong>
              </button>
            ))}
          </div>
        )}
      </div>
    </PanelShell>
  )
}

interface IntentionsPanelProps {
  identity: ClientIdentity
  intentions: Intention[]
  timer: TimerControls
  onCreate: (input: {
    title: string
    steps: Pick<IntentionStep, 'text'>[]
    isPublic: boolean
    durationMinutes?: number
  }) => void
  onUpdate: (input: {
    intentionId: string
    status?: 'in_progress' | 'completed'
    stepId?: string
    title?: string
    steps?: Pick<IntentionStep, 'text'>[]
    isPublic?: boolean
    durationMinutes?: number | null
  }) => void
  onReact: (id: string, emoji: string) => void
  onDelete: (id: string) => void
  onClose: () => void
}

function VisibilityMenu({ onSelect, onClose }: { onSelect: (isPublic: boolean) => void; onClose: () => void }) {
  return (
    <div className="visibility-menu" role="menu">
      <strong>Who should see this?</strong>
      <button type="button" onClick={() => { onSelect(true); onClose() }}><Globe2 size={15} /> Everyone</button>
      <button type="button" onClick={() => { onSelect(false); onClose() }}><Lock size={15} /> Only me</button>
    </div>
  )
}

function ReactionChips({ intention, viewerId, onReact }: { intention: Intention; viewerId: string; onReact: (id: string, emoji: string) => void }) {
  return (
    <span className="reaction-options">
      {['❤️'].map((emoji) => {
        const users = intention.reactions[emoji] ?? []
        return (
          <button
            className={`${users.length ? 'has-count' : 'ghost'} ${users.includes(viewerId) ? 'mine' : ''}`}
            key={emoji}
            onClick={() => onReact(intention.id, emoji)}
          >
            {emoji}{users.length > 0 && <small>{users.length}</small>}
          </button>
        )
      })}
    </span>
  )
}

const CONFETTI_COLORS = ['#3ca57f', '#e2b93b', '#d26d93', '#557bd3', '#bd7351']

function Confetti() {
  return (
    <span aria-hidden className="confetti">
      {Array.from({ length: 14 }, (_, index) => (
        <i
          key={index}
          style={{
            left: `${6 + (index * 89) % 88}%`,
            background: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
            animationDelay: `${(index % 5) * 70}ms`,
            transform: `rotate(${(index * 47) % 360}deg)`,
          }}
        />
      ))}
    </span>
  )
}

function timeAgo(date: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 60_000))
  if (minutes < 1) return 'now'
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  return `${hours}h`
}

export function IntentionsPanel({
  identity,
  intentions,
  timer,
  onCreate,
  onUpdate,
  onReact,
  onDelete,
  onClose,
}: IntentionsPanelProps) {
  const [title, setTitle] = useState('')
  const [steps, setSteps] = useState<string[]>([])
  const [isPublic, setIsPublic] = useState(true)
  const [withTimer, setWithTimer] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [visibilityMenu, setVisibilityMenu] = useState<'form' | 'card' | null>(null)
  const [timerEditor, setTimerEditor] = useState(false)
  const [timerMinutes, setTimerMinutes] = useState(timer.durationMinutes)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [celebrating, setCelebrating] = useState<string | null>(null)
  const [doneMoment, setDoneMoment] = useState<string | null>(null)
  const activeOwn = intentions.find((intention) => intention.userId === identity.id && intention.status === 'in_progress')

  const resetForm = () => {
    setTitle('')
    setSteps([])
    setEditingId(null)
    setWithTimer(false)
    setIsPublic(true)
    setVisibilityMenu(null)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!title.trim()) return
    const input = { title: title.trim(), steps: steps.filter(Boolean).map((text) => ({ text })), isPublic, durationMinutes: withTimer ? timer.durationMinutes : undefined }
    if (editingId) onUpdate({ intentionId: editingId, ...input })
    else {
      onCreate(input)
      // setting an intention with a timer starts the countdown right away
      if (withTimer && !timer.running) timer.startWith(timer.durationMinutes)
    }
    resetForm()
  }

  const complete = (intention: Intention) => {
    resetForm()
    setCelebrating(intention.id)
    window.setTimeout(() => setCelebrating((current) => (current === intention.id ? null : current)), 1_800)
    setDoneMoment(intention.title)
    window.setTimeout(() => setDoneMoment((current) => (current === intention.title ? null : current)), 2_800)
    onUpdate({ intentionId: intention.id, status: 'completed' })
  }

  return (
    <PanelShell title="Intentions" onClose={onClose}>
      {doneMoment && (
        <div className="task-done-moment" onClick={() => setDoneMoment(null)}>
          {Array.from({ length: 20 }, (_, index) => (
            <i
              key={index}
              style={{
                left: `${(index * 53) % 100}%`,
                background: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
                animationDelay: `${(index % 7) * 120}ms`,
              }}
            />
          ))}
          <img src={avatarSrc(identity)} alt="" className="task-done-animal" />
          <strong>Task done! 🎉</strong>
          <small>{doneMoment}</small>
        </div>
      )}
      <div className="panel-scroll intentions-panel">
        {activeOwn && !editingId ? (
          <article className="my-intention">
            <div className="author-row">
              <span className="mini-avatar" style={{ '--avatar': identity.color } as React.CSSProperties}>{identity.name[0]}</span>
              <strong>My intention</strong>
              <button
                type="button"
                className="timer-link"
                onClick={() => {
                  setTimerMinutes(activeOwn.durationMinutes ?? timer.durationMinutes)
                  setTimerEditor(!timerEditor)
                }}
              >
                <Clock3 size={14} />
                {activeOwn.durationMinutes
                  ? timer.running
                    ? <><span className="timer-value">{timer.display}</span><span className="timer-edit-hint">Edit timer</span></>
                    : `${activeOwn.durationMinutes} min`
                  : 'Set a timer'}
              </button>
            </div>
            <p>{activeOwn.title}</p>
            {activeOwn.steps.length > 0 && (
              <ul className="my-intention-steps">
                {activeOwn.steps.map((step) => (
                  <li key={step.id} className={step.completed ? 'done' : ''}>
                    <button type="button" onClick={() => onUpdate({ intentionId: activeOwn.id, stepId: step.id })}>
                      {step.completed ? <Check size={13} /> : <Circle size={13} />}
                    </button>
                    {step.text}
                  </li>
                ))}
              </ul>
            )}
            {timerEditor && (
              <div className="card-timer-editor">
                <button type="button" onClick={() => setTimerMinutes((current) => Math.max(1, current - 5))}><Minus size={14} /></button>
                <strong>{timerMinutes} min</strong>
                <button type="button" onClick={() => setTimerMinutes((current) => Math.min(240, current + 5))}><Plus size={14} /></button>
                <button
                  type="button"
                  className="start-timer"
                  onClick={() => {
                    timer.startWith(timerMinutes)
                    onUpdate({ intentionId: activeOwn.id, durationMinutes: timerMinutes })
                    setTimerEditor(false)
                  }}
                >
                  {timer.running ? 'Restart' : 'Start'}
                </button>
              </div>
            )}
            <div className="my-intention-actions">
              <ReactionChips intention={activeOwn} viewerId={identity.id} onReact={onReact} />
              <span className="visibility-anchor">
                <button type="button" className="visibility-inline" onClick={() => setVisibilityMenu(visibilityMenu === 'card' ? null : 'card')} title={activeOwn.isPublic ? 'Visible to the room' : 'Only visible to you'}>
                  {activeOwn.isPublic ? <Globe2 size={15} /> : <Lock size={15} />}
                </button>
                {visibilityMenu === 'card' && (
                  <VisibilityMenu
                    onClose={() => setVisibilityMenu(null)}
                    onSelect={(nextPublic) => onUpdate({ intentionId: activeOwn.id, isPublic: nextPublic })}
                  />
                )}
              </span>
              <button onClick={() => { setEditingId(activeOwn.id); setTitle(activeOwn.title); setSteps(activeOwn.steps.map((step) => step.text)); setIsPublic(activeOwn.isPublic); setWithTimer(Boolean(activeOwn.durationMinutes)); setTimerEditor(false) }}>Edit</button>
              <button className="complete-intention" onClick={() => complete(activeOwn)}>Complete</button>
            </div>
          </article>
        ) : <form className="intention-form" onSubmit={submit}>
          <div className="author-row">
            <span className="mini-avatar" style={{ '--avatar': identity.color } as React.CSSProperties}>
              {identity.name[0].toUpperCase()}
            </span>
            <strong>{editingId ? 'Edit intention' : 'New intention'}</strong>
            <button type="button" className="timer-link" onClick={() => setWithTimer(!withTimer)}>
              <Clock3 size={14} /> {withTimer ? `${timer.durationMinutes} min` : 'Set a timer'}
            </button>
          </div>
          <textarea
            maxLength={150}
            placeholder="I intend to…"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) event.currentTarget.form?.requestSubmit()
            }}
          />
          <div className="form-meta"><span>{title.length}/150</span></div>
          {steps.map((step, index) => (
            <div className="step-input" key={index}>
              <Circle size={14} />
              <input
                autoFocus
                maxLength={100}
                placeholder={`Step ${index + 1}`}
                value={step}
                onChange={(event) =>
                  setSteps((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))
                }
              />
              <button type="button" onClick={() => setSteps((current) => current.filter((_, itemIndex) => itemIndex !== index))}><X size={14} /></button>
            </div>
          ))}
          <div className="intention-form-footer">
            <button type="button" className="add-step" disabled={steps.length >= 3} onClick={() => setSteps([...steps, ''])}>
              + add step ({steps.length} / 3)
            </button>
            {editingId && <button type="button" className="delete-intention" onClick={() => setConfirmDelete(true)}>Delete</button>}
            {editingId && <button type="button" className="cancel-edit" onClick={resetForm}>Cancel</button>}
            <span className="visibility-anchor">
              <button type="button" className="visibility" onClick={() => setVisibilityMenu(visibilityMenu === 'form' ? null : 'form')} title={isPublic ? 'Visible to the room' : 'Only visible to you'}>
                {isPublic ? <Globe2 size={17} /> : <Lock size={17} />}
              </button>
              {visibilityMenu === 'form' && (
                <VisibilityMenu onClose={() => setVisibilityMenu(null)} onSelect={setIsPublic} />
              )}
            </span>
            <button className="set-intention" disabled={!title.trim()}>{editingId ? 'Update' : 'Set intention'}</button>
          </div>
        </form>}

        {confirmDelete && editingId && (
          <div className="modal-backdrop" onClick={() => setConfirmDelete(false)}>
            <div className="confirm-dialog" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
              <h3>Delete intention</h3>
              <p>This is a permanent deletion. Set the intention to private if you only want to hide it from other people.</p>
              <div className="confirm-actions">
                <button onClick={() => setConfirmDelete(false)}>Cancel</button>
                <button className="danger" onClick={() => { onDelete(editingId); setConfirmDelete(false); resetForm() }}>Delete</button>
              </div>
            </div>
          </div>
        )}

        <div className="intention-feed">
          {intentions.filter((intention) => intention.id !== activeOwn?.id).map((intention) => {
            const own = intention.userId === identity.id
            return (
              <article className={`intention-card ${intention.status === 'completed' ? 'completed' : ''} ${celebrating === intention.id ? 'celebrate' : ''}`} key={intention.id}>
                {celebrating === intention.id && <Confetti />}
                <div className="feed-author">
                  <span className="mini-avatar" style={{ '--avatar': intention.userColor } as React.CSSProperties}>{intention.userName[0]}</span>
                  <div><strong>{intention.userName}</strong>{intention.userRole && <small className="feed-role">{intention.userRole}</small>}</div>
                  <small className="feed-time">{timeAgo(intention.createdAt)}</small>
                  {!intention.isPublic && <Lock size={13} />}
                </div>
                <p>{intention.title}</p>
                {intention.steps.length > 0 && (
                  <ul>
                    {intention.steps.map((step) => (
                      <li key={step.id} className={step.completed ? 'done' : ''}>
                        <button disabled={!own} onClick={() => onUpdate({ intentionId: intention.id, stepId: step.id })}>
                          {step.completed ? <Check size={13} /> : <Circle size={13} />}
                        </button>
                        {step.text}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="intention-status-row">
                  {own ? (
                    <button
                      className="status-button"
                      onClick={() => onUpdate({
                        intentionId: intention.id,
                        status: intention.status === 'completed' ? 'in_progress' : 'completed',
                      })}
                    >
                      <span className="live-dot" />
                      {intention.status === 'completed' ? 'Completed' : 'In progress'}
                    </button>
                  ) : (
                    <span className="status-label"><span className="live-dot" />{intention.status === 'completed' ? 'Completed' : 'In progress'}</span>
                  )}
                  <ReactionChips intention={intention} viewerId={identity.id} onReact={onReact} />
                </div>
              </article>
            )
          })}
        </div>
      </div>
    </PanelShell>
  )
}

interface ChatPanelProps {
  identity: ClientIdentity
  messages: ChatMessage[]
  onSend: (body: string) => void
  onReact: (id: string, emoji: string) => void
  onClose: () => void
}

export function ChatPanel({ identity, messages, onSend, onReact, onClose }: ChatPanelProps) {
  const [body, setBody] = useState('')
  const feedRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight })
  }, [messages])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!body.trim()) return
    onSend(body.trim())
    setBody('')
  }

  return (
    <PanelShell title="Chat" onClose={onClose}>
      <div className="chat-panel">
        <div className="chat-feed" ref={feedRef}>
          {messages.length === 0 && (
            <div className="empty-panel" aria-label="No messages" />
          )}
          {messages.map((message) => (
            <article className={`chat-message ${message.userId === identity.id ? 'own' : ''}`} key={message.id}>
              <span className="mini-avatar" style={{ '--avatar': message.userColor } as React.CSSProperties}>{message.userName[0]}</span>
              <div>
                <div className="message-meta"><strong>{message.userName}</strong><time>{timeAgo(message.createdAt)}</time></div>
                <p>{message.body}</p>
                <div className="message-reactions">
                  {Object.entries(message.reactions).map(([emoji, users]) => (
                    <button className={users.includes(identity.id) ? 'mine' : ''} key={emoji} onClick={() => onReact(message.id, emoji)}>{emoji} {users.length}</button>
                  ))}
                  {['❤️'].filter((emoji) => !message.reactions[emoji]).map((emoji) => (
                    <button className="quick-react" key={emoji} onClick={() => onReact(message.id, emoji)}>{emoji}</button>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
        <form className="chat-composer" onSubmit={submit}>
          <input maxLength={500} placeholder="Message the room…" value={body} onChange={(event) => setBody(event.target.value)} />
          <button disabled={!body.trim()} aria-label="Send"><Send size={17} /></button>
        </form>
      </div>
    </PanelShell>
  )
}

interface PeoplePanelProps {
  users: UserProfile[]
  selectedUser: UserProfile | null
  currentUserId: string
  self?: ClientIdentity
  hiddenIds: ReadonlySet<string>
  onSelectUser: (user: UserProfile | null) => void
  onSaveProfile: (profile: ProfileInput) => Promise<void>
  onUploadAvatar: (file: File) => Promise<void>
  onToggleHidden: (userId: string) => void
  onReport: (userId: string) => void
  onClose: () => void
}

type ProfileInput = Pick<ClientIdentity, 'name' | 'role' | 'bio' | 'location' | 'company' | 'profilePublic' | 'showActivity' | 'avatarUrl' | 'locationPrecision' | 'hereFor' | 'citizenship' | 'basedIn' | 'birthday' | 'socials'>

const BIO_PLACEHOLDERS = [
  'Working remotely on my startup…',
  'Writing my thesis, one session at a time…',
  'Building something new every morning…',
]

/** Searchable country picker: type to filter, click to choose. */
function CountrySelect({ value, onChange, placeholder }: { value: string; onChange: (code: string) => void; placeholder: string }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const matches = COUNTRY_CODES
    .filter((code) => countryName(code).toLowerCase().includes(query.trim().toLowerCase()))
    .slice(0, 12)
  return (
    <div className={`country-select ${open ? 'open' : ''}`}>
      <input
        placeholder={placeholder}
        value={open ? query : value ? `${countryFlag(value)} ${countryName(value)}` : ''}
        onFocus={() => { setOpen(true); setQuery('') }}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onChange={(event) => setQuery(event.target.value)}
      />
      {open && (
        <div className="country-menu">
          {value && <button type="button" onMouseDown={() => { onChange(''); setOpen(false) }}>✕ Clear</button>}
          {matches.map((code) => (
            <button type="button" key={code} onMouseDown={() => { onChange(code); setOpen(false) }}>
              {countryFlag(code)} {countryName(code)}
            </button>
          ))}
          {!matches.length && <p>No match</p>}
        </div>
      )}
    </div>
  )
}

/** Nomads-style multi-select dropdown: a summary button opening a checklist. */
function MultiSelect({ options, selected, onToggle, placeholder }: {
  options: ReadonlyArray<{ id: string; label: string }>
  selected: string[]
  onToggle: (id: string) => void
  placeholder: string
}) {
  const [open, setOpen] = useState(false)
  const [custom, setCustom] = useState('')
  const host = useRef<HTMLDivElement>(null)
  const labels = selected.map((id) => options.find((option) => option.id === id)?.label ?? id).filter(Boolean)

  // clicking anywhere else closes the menu
  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (host.current && !host.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  return (
    <div className={`multi-select ${open ? 'open' : ''}`} ref={host}>
      <button type="button" onClick={() => setOpen(!open)}>
        <span>{labels.length ? labels.join(' · ') : placeholder}</span>
        <ChevronLeft size={14} className="chevron" />
      </button>
      {open && (
        <div className="multi-select-menu">
          {options.map((option) => (
            <button type="button" key={option.id} className={selected.includes(option.id) ? 'active' : ''} onClick={() => onToggle(option.id)}>
              <span>{option.label}</span>
              {selected.includes(option.id) && <Check size={14} />}
            </button>
          ))}
          {selected.filter((id) => !options.some((option) => option.id === id)).map((id) => (
            <button type="button" key={id} className="active" onClick={() => onToggle(id)}>
              <span>{id}</span>
              <Check size={14} />
            </button>
          ))}
          <div className="multi-select-custom">
            <input
              maxLength={30}
              placeholder="✏️ Write my own…"
              value={custom}
              onChange={(event) => setCustom(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && custom.trim()) {
                  onToggle(custom.trim())
                  setCustom('')
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function normalizeLink(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  return /^https?:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`
}

function ProfileView({
  user,
  self,
  editable,
  hidden,
  previewSelf,
  onBack,
  onSave,
  onUploadAvatar,
  onToggleHidden,
  onReport,
}: {
  user: UserProfile
  self?: ClientIdentity
  editable: boolean
  hidden: boolean
  /** own profile rendered as strangers see it: hide the hide/report actions */
  previewSelf?: boolean
  onBack: () => void
  onSave: (profile: ProfileInput) => Promise<void>
  onUploadAvatar: (file: File) => Promise<void>
  onToggleHidden: () => void
  onReport: () => void
}) {
  const [reported, setReported] = useState(false)
  const canSeeProfile = editable || user.profilePublic !== false
  const canSeeActivity = editable || user.showActivity !== false
  const [name, setName] = useState(user.name)
  const [nameEditing, setNameEditing] = useState(false)
  const [role, setRole] = useState(user.role)
  const [bio, setBio] = useState(user.bio ?? '')
  const [profilePublic, setProfilePublic] = useState(user.profilePublic ?? true)
  const [showActivity, setShowActivity] = useState(user.showActivity ?? true)
  const [locationPrecision, setLocationPrecision] = useState<LocationPrecision>(user.locationPrecision ?? 'city')
  const [hereFor, setHereFor] = useState<string[]>(user.hereFor ?? [])
  const [citizenship, setCitizenship] = useState(self?.citizenship ?? user.citizenship ?? '')
  const [basedIn, setBasedIn] = useState(self?.basedIn ?? user.basedIn ?? '')
  const [birthday, setBirthday] = useState(self?.birthday ?? '')
  const [socials, setSocials] = useState<SocialLinks>(self?.socials ?? user.socials ?? {})
  const [avatarMenu, setAvatarMenu] = useState(false)
  const [customRole, setCustomRole] = useState(false)
  const [savedFlash, setSavedFlash] = useState(false)
  const [together, setTogether] = useState<{
    partners: Array<{ id: string; name: string; color: string; avatarUrl?: string; countryCode?: string; seconds: number }>
    countries: string[]
    standing?: Standing | null
  } | null>(null)

  useEffect(() => {
    if (!canSeeActivity) return
    let cancelled = false
    void fetch(`/api/profile/${user.id}/together?preview=1`, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => { if (!cancelled && data) setTogether(data) })
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [user.id, canSeeActivity])
  const bioPlaceholder = BIO_PLACEHOLDERS[hashText(user.id) % BIO_PLACEHOLDERS.length]

  const formInput = (overrides?: Partial<ProfileInput>): ProfileInput => ({
    name: name.trim() || user.name,
    role: role.trim(),
    bio: bio.trim(),
    location: user.location ?? '',
    company: '',
    profilePublic,
    showActivity,
    locationPrecision,
    hereFor,
    citizenship,
    basedIn,
    birthday,
    socials: {
      x: normalizeLink(socials.x ?? ''),
      instagram: normalizeLink(socials.instagram ?? ''),
      linkedin: normalizeLink(socials.linkedin ?? ''),
      website: normalizeLink(socials.website ?? ''),
    },
    ...overrides,
  })

  // everything autosaves: touch a field, walk away, it is stored
  const firstRun = useRef(true)
  const saveRef = useRef(formInput)
  saveRef.current = formInput
  useEffect(() => {
    if (!editable) return
    if (firstRun.current) { firstRun.current = false; return }
    const timeout = window.setTimeout(() => {
      void onSave(saveRef.current()).then(() => {
        setSavedFlash(true)
        window.setTimeout(() => setSavedFlash(false), 1_600)
      }).catch(() => undefined)
    }, 700)
    return () => window.clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, role, bio, profilePublic, showActivity, locationPrecision, hereFor, citizenship, basedIn, birthday, socials, editable])

  const flag = user.basedIn || user.countryCode
  // badges only show what someone actually earned: an empty rack is noise
  const earnedSessionBadges = SESSION_BADGES.filter((milestone) => user.sessions >= milestone)
  const earnedWeekBadges = WEEK_BADGES.filter((weeks) => (user.weekStreak ?? 0) >= weeks)
  const socialEntries: Array<[string, string, React.ReactNode]> = user.socials
    ? ([
      ['x', user.socials.x ?? '', ' 𝕏 '],
      ['instagram', user.socials.instagram ?? '', 'IG'],
      ['linkedin', user.socials.linkedin ?? '', 'in'],
      ['website', user.socials.website ?? '', <Link2 key="w" size={15} />],
    ] as Array<[string, string, React.ReactNode]>).filter(([, url]) => Boolean(url))
    : []

  return (
    <div className="profile-panel panel-scroll">
      <button className="profile-back" onClick={onBack}><ChevronLeft size={17} /> People</button>
      <div className="profile-hero">
        <span className="profile-avatar-wrap">
          <button
            className={`profile-avatar ${editable ? 'editable' : ''}`}
            style={{ '--avatar': user.color } as React.CSSProperties}
            onClick={() => editable && setAvatarMenu(!avatarMenu)}
            aria-label={editable ? 'Edit avatar' : undefined}
          >
            <img src={avatarSrc(user)} alt="" />
          </button>
          <span className="presence-dot" title="Online now" />
          {editable && avatarMenu && (
            <div className="avatar-menu">
              <label className="avatar-upload">
                <Camera size={15} /> Upload a photo
                <input aria-label="Photo" accept="image/jpeg,image/png,image/webp" type="file" onChange={(event) => { const file = event.target.files?.[0]; if (file) { void onUploadAvatar(file); setAvatarMenu(false) } }} />
              </label>
              <p>or pick a character</p>
              <div className="avatar-picker">
                {ANIMAL_AVATARS.map((animal) => {
                  const url = animalAvatarUrl(animal)
                  return (
                    <button key={animal} type="button" className={avatarSrc(user) === url ? 'active' : ''} aria-label={animal} onClick={() => { void onSave(formInput({ avatarUrl: url })); setAvatarMenu(false) }}>
                      <img src={url} alt="" />
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </span>
        {canSeeActivity && <SessionBadge sessions={user.sessions} />}
        {editable && nameEditing ? (
          <input
            className="profile-name-input"
            autoFocus
            maxLength={40}
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => setNameEditing(false)}
            onKeyDown={(event) => { if (event.key === 'Enter') setNameEditing(false) }}
          />
        ) : (
          <h3 className={editable ? 'editable-name' : ''} onClick={() => editable && setNameEditing(true)}>
            {name || user.name}
            {editable && <Pencil size={14} />}
          </h3>
        )}
        {user.role && !editable && <p>{user.role}</p>}
        {(flag || user.age !== undefined) && canSeeProfile && (
          <p className="profile-meta-line">
            {flag ? `${countryFlag(flag)} ${countryName(flag)}` : ''}
            {flag && user.age !== undefined ? ' · ' : ''}
            {user.age !== undefined ? `${user.age}` : ''}
          </p>
        )}
        {canSeeActivity && (() => {
          const claim = standingClaim(together?.standing)
          if (!claim) return null
          if (claim.kind === 'global') return <p className="profile-standing">#{claim.rank} worldwide</p>
          if (claim.kind === 'country') {
            return <p className="profile-standing">#{claim.rank} in {countryName(claim.country)} {countryFlag(claim.country)}</p>
          }
          return (
            <p className="profile-standing">
              Representing {countryName(claim.country)} {countryFlag(claim.country)} · one of {claim.count}
            </p>
          )
        })()}
        {canSeeActivity && (
          <div className="profile-stats">
            {formatFocusTime(user.focusSeconds) && <div><b>{formatFocusTime(user.focusSeconds)}</b><span>focused</span></div>}
            <div><b>{user.sessions}</b><span>{user.sessions === 1 ? 'session' : 'sessions'}</span></div>
            {user.createdAt && <div><b>{new Date(user.createdAt).toLocaleDateString('en', { month: 'short', year: 'numeric' })}</b><span>member since</span></div>}
          </div>
        )}
        {!editable && canSeeProfile && (user.hereFor?.length ?? 0) > 0 && (
          <div className="herefor-chips view">
            {user.hereFor?.map((id) => hereForLabel(id)).filter(Boolean).map((label) => <span key={label}>{label}</span>)}
          </div>
        )}
        {!editable && socialEntries.length > 0 && canSeeProfile && (
          <div className="profile-socials">
            {socialEntries.map(([key, url, icon]) => (
              <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={key}>{icon}</a>
            ))}
          </div>
        )}
        {editable && savedFlash && <p className="autosave-note flash">Saved ✓</p>}
      </div>
      {editable && (
        <form className="profile-form" onSubmit={(event) => event.preventDefault()}>
          <label><span>Bio</span><textarea maxLength={240} placeholder={bioPlaceholder} value={bio} onChange={(event) => setBio(event.target.value)} /></label>
          <label><span>I am</span>
            <select
              value={ROLE_CHIPS.includes(role as (typeof ROLE_CHIPS)[number]) ? role : role ? '__custom' : ''}
              onChange={(event) => {
                if (event.target.value === '__custom') { setCustomRole(true); return }
                setCustomRole(false)
                setRole(event.target.value)
              }}
            >
              <option value="">Choose your role…</option>
              {ROLE_CHIPS.map((chip) => <option key={chip} value={chip}>{chip}</option>)}
              <option value="__custom">✏️ Write my own…</option>
            </select>
          </label>
          {(customRole || (role !== '' && !ROLE_CHIPS.includes(role as (typeof ROLE_CHIPS)[number]))) && (
            <input
              maxLength={80}
              placeholder="🧑‍🚀 Anything you like — emoji welcome"
              value={ROLE_CHIPS.includes(role as (typeof ROLE_CHIPS)[number]) ? '' : role}
              onChange={(event) => setRole(event.target.value)}
            />
          )}
          <label><span>Here for</span></label>
          <MultiSelect
            options={HERE_FOR_OPTIONS}
            selected={hereFor}
            onToggle={(id) => setHereFor((previous) => previous.includes(id) ? previous.filter((entry) => entry !== id) : [...previous, id])}
            placeholder="What brings you here?"
          />
          <div className="profile-form-row">
            <label><span>Citizenship</span><CountrySelect value={citizenship} onChange={setCitizenship} placeholder="Choose…" /></label>
            <label><span>Based in</span><CountrySelect value={basedIn} onChange={setBasedIn} placeholder="Choose…" /></label>
          </div>
          <div className="profile-form-row">
            <label><span>Date of birth</span><input type="date" value={birthday} onChange={(event) => setBirthday(event.target.value)} /></label>
            <label><span>On the globe</span>
              <select value={locationPrecision} onChange={(event) => setLocationPrecision(event.target.value as LocationPrecision)}>
                <option value="city">Show my city</option>
                <option value="country">Country only</option>
                <option value="hidden">Antarctica 🐧</option>
              </select>
            </label>
          </div>
          <label><span>Social links</span></label>
          <div className="profile-form-row">
            <input placeholder="x.com/…" value={socials.x ?? ''} onChange={(event) => setSocials({ ...socials, x: event.target.value })} />
            <input placeholder="instagram.com/…" value={socials.instagram ?? ''} onChange={(event) => setSocials({ ...socials, instagram: event.target.value })} />
          </div>
          <div className="profile-form-row">
            <input placeholder="linkedin.com/in/…" value={socials.linkedin ?? ''} onChange={(event) => setSocials({ ...socials, linkedin: event.target.value })} />
            <input placeholder="your website" value={socials.website ?? ''} onChange={(event) => setSocials({ ...socials, website: event.target.value })} />
          </div>
          <label className="profile-toggle"><span>Public profile</span><input className="switch" type="checkbox" checked={profilePublic} onChange={(event) => setProfilePublic(event.target.checked)} /></label>
          <label className="profile-toggle"><span>Show activity</span><input className="switch" type="checkbox" checked={showActivity} onChange={(event) => setShowActivity(event.target.checked)} /></label>
        </form>
      )}
      {!editable && canSeeProfile && user.bio && <p className="profile-bio">{user.bio}</p>}
      {!editable && !previewSelf && (
        <div className="profile-actions">
          <button onClick={onToggleHidden}>{hidden ? 'Show video again' : 'Hide user'}</button>
          <button
            disabled={reported}
            onClick={() => { setReported(true); onReport() }}
          >
            {reported ? 'Reported' : 'Report user'}
          </button>
        </div>
      )}
      {canSeeActivity && (together?.partners.length ?? 0) > 0 && (
        <section className="profile-together">
          <h4>Focused together most</h4>
          <div className="together-row">
            {together?.partners.map((partner) => (
              <span
                key={partner.id}
                className="together-avatar"
                style={{ '--avatar': partner.color } as React.CSSProperties}
                title={`${partner.name} · ${formatFocusTime(partner.seconds) ?? '<1 min'} together`}
              >
                <img src={avatarSrc(partner)} alt={partner.name} />
              </span>
            ))}
          </div>
        </section>
      )}
      {canSeeActivity && (together?.countries.length ?? 0) > 0 && (
        <section className="profile-together">
          <h4>Flags collected ({together?.countries.length})</h4>
          <p className="flags-sub">Focused with people from {together?.countries.length} {together?.countries.length === 1 ? 'country' : 'countries'}</p>
          <div className="flag-wall">
            {together?.countries.map((code) => <span key={code} title={countryName(code)}>{countryFlag(code)}</span>)}
          </div>
        </section>
      )}
      {canSeeActivity && (earnedSessionBadges.length > 0 || earnedWeekBadges.length > 0) && <section className="profile-patches">
        {earnedSessionBadges.length > 0 && <>
          <h4>Session milestones</h4>
          <div>{earnedSessionBadges.map((milestone) => <span key={milestone}><SessionBadge sessions={milestone} /></span>)}</div>
        </>}
        {earnedWeekBadges.length > 0 && <>
          <h4>Weekly streaks</h4>
          <div className="week-badges">{earnedWeekBadges.map((weeks) => (
            <span
              className={`week-badge ${weeks === 52 ? 'year' : ''}`}
              key={weeks}
              title={weeks === 52 ? 'Join a session every week for a full year' : `Join a session every week for ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`}
            >
              {weekBadgeLabel(weeks)}
            </span>
          ))}</div>
        </>}
      </section>}
    </div>
  )
}

function formatFocusTime(seconds?: number): string | null {
  if (!seconds || seconds < 60) return null
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`
  const hours = seconds / 3600
  return hours < 10 ? `${(Math.round(hours * 10) / 10).toString()} h` : `${Math.round(hours)} h`
}

function hashText(text: string): number {
  let hash = 7
  for (let index = 0; index < text.length; index += 1) hash = (hash * 31 + text.charCodeAt(index)) >>> 0
  return hash
}

export function PeoplePanel({ users, selectedUser, currentUserId, self, hiddenIds, onSelectUser, onSaveProfile, onUploadAvatar, onToggleHidden, onReport, onClose }: PeoplePanelProps) {
  const [viewAsOther, setViewAsOther] = useState(false)
  const selectedId = selectedUser?.id
  useEffect(() => { setViewAsOther(false) }, [selectedId])

  if (selectedUser) {
    const isSelf = selectedUser.id === currentUserId
    return (
      <PanelShell title="Profile" onClose={onClose}>
        {isSelf && (
          <button
            className={`view-as-toggle ${viewAsOther ? 'active' : ''}`}
            onClick={() => setViewAsOther(!viewAsOther)}
          >
            {viewAsOther ? <><Pencil size={13} /> Edit profile</> : <><Eye size={13} /> View as others</>}
          </button>
        )}
        <ProfileView
          key={`${selectedUser.id}${viewAsOther ? '-preview' : ''}`}
          user={selectedUser}
          self={viewAsOther ? undefined : self}
          editable={isSelf && !viewAsOther}
          previewSelf={isSelf && viewAsOther}
          hidden={hiddenIds.has(selectedUser.id)}
          onBack={() => onSelectUser(null)}
          onSave={onSaveProfile}
          onUploadAvatar={onUploadAvatar}
          onToggleHidden={() => onToggleHidden(selectedUser.id)}
          onReport={() => onReport(selectedUser.id)}
        />
      </PanelShell>
    )
  }

  return (
    <PanelShell title={`People (${users.length})`} onClose={onClose}>
      <div className="people-list panel-scroll">
        {users.map((user) => (
          <button className="person-row" key={user.id} onClick={() => onSelectUser(user)}>
            <span className="person-avatar" style={{ '--avatar': user.color } as React.CSSProperties}><img src={avatarSrc(user)} alt="" /><span className="presence-dot small" /></span>
            <span className="person-copy">
              <strong>{user.name} {(user.basedIn || user.countryCode) ? countryFlag(user.basedIn || user.countryCode || '') : ''}</strong>
              {(user.role || user.bio) && <small>{[user.role, user.bio].filter(Boolean).join(' · ')}</small>}
            </span>
            {(user.id === currentUserId || user.showActivity !== false) && <SessionBadge sessions={user.sessions} />}
          </button>
        ))}
      </div>
    </PanelShell>
  )
}

export function CameraOffDialog({ productName, seconds, onTurnOn, onStayOff }: { productName: string; seconds: number; onTurnOn: () => void; onStayOff: () => void }) {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, '0')
  const remainder = (seconds % 60).toString().padStart(2, '0')
  return (
    <div className="modal-backdrop">
      <div className="camera-dialog" role="dialog" aria-modal="true">
        <div className="camera-dialog-title"><h2>Are you still focusing?</h2><time><Clock3 /> {minutes}:{remainder}</time></div>
        <h3>{productName} works best if we keep our cameras on as much as possible.</h3>
        <p>You get the benefit of body doubling from everyone in the room, and give that same focus signal back.</p>
        <div className="camera-dialog-actions">
          <button className="primary-button" onClick={onTurnOn}>Turn my camera back on</button>
          <button className="text-button" onClick={onStayOff}>Stay with my camera off</button>
        </div>
      </div>
    </div>
  )
}
